// ** React Imports
import { useEffect, useState } from "react";

// ** MUI Imports
import Stepper from "@mui/material/Stepper";
import StepLabel from "@mui/material/StepLabel";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";
import MuiStep from "@mui/material/Step";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import CheckIcon from "@mui/icons-material/Check";
import { CardContent, useMediaQuery } from "@mui/material";

// ** Step Components
import StepCompanyDetails from "./StepCompanyDetails";
import StepPersonalDetails from "./StepPersonalDetails";
import StepAddEmployee from "./StepAddEmployee";
import StepAddCustomer from "./StepAddCustomer";

// ** Styled Components
import StepperWrapper from "../../../../components/styles/mui/stepper";
import StepInviteUsers from "./StepInviteUsers";
import StepAddServices from "./StepAddServices";
import StepAddBundles from "./StepAddBundles";
import StepAddAssets from "./StepAddAssets";
import StepAddMaterials from "./StepAddMaterials";
import StepConnecttoStripe from "./StepConnecttostripe";
import { useLocation } from "react-router-dom";
import { GetEmployees } from "screens/Employees/logic/GetEmployees";
import { GetOwner } from "../logic/getOwner";
import { GetCompany } from "../logic/getCompany";

const StepperHeaderContainer = styled(CardContent)(({ theme }) => ({
  justifyContent: 'center',
  [theme.breakpoints.down("md")]: {
    borderRight: 0,
    borderBottom: "none",
    padding: theme.spacing(2),
  },
}));

const steps = [
  {
    title: "Personal Details",
    icon: "tabler:circle-number-1",
  },
  {
    title: "Company Details",
    icon: "tabler:circle-number-2",
  },
  {
    title: "Payment Processing",
    icon: "tabler:circle-number-3",
  },
  {
    title: "Add Employees",
    icon: "tabler:circle-number-4",
  },
  {
    title: "Invite Users",
    icon: "tabler:circle-number-5",
  },
  {
    title: "Add Services",
    icon: "tabler:circle-number-6",
  },
  {
    title: "Add Bundles",
    icon: "tabler:circle-number-7",
  },
  {
    title: "Add Assets",
    icon: "tabler:circle-number-8",
  },
  {
    title: "Add Materials",
    icon: "tabler:circle-number-9",
  },
  {
    title: "Add Customers",
    icon: "tabler:circle-number-10",
  },
];

const Step = styled(MuiStep)(({ theme }) => ({
  padding: 0,
  "& .MuiStepLabel-iconContainer": {
    display: "none",
  },
  "& .step-subtitle": {
    color: `${theme.palette.text.disabled} !important`,
  },
  "& + svg": {
    color: theme.palette.text.disabled,
  },
  "&.Mui-completed .step-title": {
    color: theme.palette.text.disabled,
  },
  "&.Mui-completed + svg": {
    color: theme.palette.primary.main,
  },
  [theme.breakpoints.down("md")]: {
    "&:not(:last-child)": {
      marginBottom: 0,
    },
    "& + svg": {
      display: "none",
    },
  },
  [theme.breakpoints.up("md")]: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    "&:first-of-type": {
      marginLeft: 0,
    },
    "&:last-of-type": {
      marginRight: 0,
    },
  },
}));

const RegisterMultiSteps = ({ setMainStep, navigate }) => {
  
  const location = useLocation();
  const initial = typeof location.state?.step === "number"
    ? location.state.step
    : 0;

  const pretabvalue = parseInt((window.location.hash).replace('#','') || "0");
  // ** States
  const [activeStep, setActiveStep] = useState(pretabvalue || initial);
  const [message, setMessage] = useState("");
  const [hoveredStep, setHoveredStep] = useState(null);
  const [mobileTooltip, setMobileTooltip] = useState(null);
  
  // Check if device is mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [personalDetails, setPersonalDetails] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    referredBy: "",
  });
  const [companyDetails, setCompanyDetails] = useState({
    companyName: "",
    industryData: {},
    timeZone: "",
    teamSize: null,
    annualRevenue: null,
    companyAddress: "",
    street2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    selectedItems: [],
    companyEmail: "",
    companyPhone: "",
  });

  const handleStepChange = (newValue) => {
    setActiveStep(newValue);    
    navigate(`#${newValue}`); // Navigate to the new tab    
  };

  useEffect(() => {
    setMainStep(activeStep);
  }, [activeStep]);


  useEffect(() => {
    if(activeStep === 0){
      fetchPersonalDetails();
    }
    if(activeStep === 1){
      fetchCompanyDetails();
    }
  }, [activeStep]);

  const fetchPersonalDetails = async () => {
    try {
      const response = await GetOwner(navigate);

      if (!response.isError ) {
        const data = response.result;
        setPersonalDetails({
          firstName: data[0].firstName || "",
          lastName: data[0].lastName || "",
          phoneNumber: data[0].phoneNumbers? data[0].phoneNumbers.find(x => x.isPrimary).phoneNo : "",
          referredBy: data[0].referredBy || "",
        });
      } else {
        console.error('Failed to fetch personal details');
      }
    } catch (error) {
      console.error('Error fetching personal details:', error);
    }
  }
  const fetchCompanyDetails = async () => {
    try {
      const response = await GetCompany(navigate);

      if (!response.isError ) {
        const data = response.result;
        setCompanyDetails({
          companyName: data.companyName || "",
          industryData: data.industryData || {},
          timeZone: data.timeZone || "",
          teamSize: data.teamSize || null,
          annualRevenue: data.annualRevenue || null,
          companyAddress: data.street1 || "",
          street2: data.street2 || "",
          city: data.city || "",
          state: data.state || "",
          zipCode: data.zipCode || "",
          country: data.country || "",
          companyEmail: data.companyEmail || "",
          companyPhone: data.companyPhone || "",
        }
        );
      } else {
        console.error('Failed to fetch personal details');
      }
    } catch (error) {
      console.error('Error fetching personal details:', error);
    }
  }

  const handleNext = () => {
    setMessage("");
    setActiveStep((s) => {
      navigate(`#${s + 1}`); // Navigate to the new tab
      return (s + 1);
    });
  };
  const handleNextInviteUsers = () => {
    setMessage("");
    // setActiveStep((s) => s + 1);
  };

  const handlePrev = () => {
    if (activeStep > 0) {
      setMessage("");
      setActiveStep((s) => {
        navigate(`#${s - 1}`); // Navigate to the new tab
        return (s - 1);
      });
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StepPersonalDetails
            handleNext={handleNext}
            personalDetails={personalDetails}
            setPersonalDetails={setPersonalDetails}
          />
        );
      case 1:
        return (
          <StepCompanyDetails
            handleNext={handleNext}
            handlePrev={handlePrev}
            companyDetails={companyDetails}
            setCompanyDetails={setCompanyDetails}
          />
        );
      case 2:
        return (
          <StepConnecttoStripe
            handleNext={handleNext}
            handlePrev={handlePrev}
          />
        );
      case 3:
        return (
          <StepAddEmployee handleNext={handleNext} handlePrev={handlePrev} />
        );
      case 4:
        return (
          <StepInviteUsers handleNextInviteUsers={handleNextInviteUsers} handleNext={handleNext} handlePrev={handlePrev} />
        );
      case 5:
        return (
          <StepAddServices handleNext={handleNext} handlePrev={handlePrev} />
        );
      case 6:
        return (
          <StepAddBundles handleNext={handleNext} handlePrev={handlePrev} />
        );
      case 7:
        return (
          <StepAddAssets handleNext={handleNext} handlePrev={handlePrev} />
        );
      case 8:
        return (
          <StepAddMaterials handleNext={handleNext} handlePrev={handlePrev} />
        );
      case 9:
        return <StepAddCustomer handlePrev={handlePrev} />;
      default:
        return null;
    }
  };

  const renderContent = () => {
    return getStepContent(activeStep);
  };

  // Mobile stepper view - just numbers with step name display
  const renderMobileStepper = () => {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%'
      }}>
        {/* Step name display */}
        {/* {mobileTooltip !== null && (
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1, 
              fontWeight: 500,
              textAlign: 'center',
              minHeight: 24
            }}
          >
            {steps[mobileTooltip]?.title}
          </Typography>
        )} */}
        
        {/* Step indicators */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          overflowX: 'auto',
          py: 1,
          gap: 1,
          width: '100%'
        }}>
          {steps.map((step, index) => {
            const isCompleted = index < activeStep;
            const isActive = index === activeStep;
            
            return (
              <Box 
                key={index}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: 25,
                  height: 25,
                  borderRadius: "50%",
                  backgroundColor: isCompleted || isActive ? "#23A25D" : "transparent",
                  border: isCompleted || isActive ? "none" : "1px solid",
                  borderColor: "grey.400",
                  color: isCompleted || isActive ? "common.white" : "text.primary",
                  fontWeight: 400,
                  fontSize: "0.875rem",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "grey.600",
                  },
                }}
                onClick={() => {
                  handleStepChange(index);
                  setMobileTooltip(index);
                }}
                onMouseEnter={() => {
                  if (!isMobile) setMobileTooltip(index);
                }}
                onTouchStart={() => setMobileTooltip(index)}
              >
                {isCompleted ? <CheckIcon sx={{ fontSize: 18 }} /> : index + 1}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  // Desktop stepper view (original - unchanged)
  const renderDesktopStepper = () => {
    return (
      <StepperWrapper>
        <Stepper activeStep={activeStep} sx={{ justifyContent: 'center' }}>
          {steps.map((step, index) => {
            const isCompleted = index < activeStep;
            const isActive = index === activeStep;
            const isHovered = hoveredStep === index;

            return (
              <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                <Step onClick={() => handleStepChange(index)}>
                  <StepLabel>
                    <Tooltip
                      title={!isActive ? step.title : ""}
                      arrow
                      placement="top"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: isCompleted && isHovered ? "#D2F3E5" : "grey.100",
                            color: isCompleted && isHovered ? "#23A26D" : "text.primary",
                            fontWeight: 520,
                            fontSize: "0.885rem",
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                          },
                        },
                        arrow: {
                          sx: {
                            color: isCompleted && isHovered ? "#D2F3E5" : "grey.100",
                          },
                        },
                      }}
                    >
                      <div
                        className="step-label"
                        onMouseEnter={() => setHoveredStep(index)}
                        onMouseLeave={() => setHoveredStep(null)}
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            backgroundColor: isCompleted || isActive ? "#23A25D" : "transparent",
                            border: isCompleted || isActive ? "none" : "1px solid",
                            borderColor: "grey.400",
                            color: isCompleted || isActive ? "common.white" : "text.primary",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                            fontWeight: 400,
                            fontSize: "0.875rem",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              borderColor: "grey.600",
                            },
                          }}
                        >
                          {isCompleted ? <CheckIcon sx={{ fontSize: 16 }} /> : index + 1}
                        </Box>

                        {isActive && (
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: "text.primary",
                              fontWeight: 550,
                            }}
                          >
                            {step.title}
                          </Typography>
                        )}
                      </div>
                    </Tooltip>
                  </StepLabel>
                </Step>

                {/* Add connector line between steps */}
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      width: 40,
                      height: 2,
                      backgroundColor: index < activeStep || index === activeStep ? "#23A25D" : "#e0e0e0",
                      mx: 1,
                      alignSelf: "center",
                      transition: "background-color 0.3s ease",
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Stepper>
      </StepperWrapper>
    );
  };

  return (
    <>
      <StepperHeaderContainer>
        {isMobile ? renderMobileStepper() : renderDesktopStepper()}
      </StepperHeaderContainer>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          p: 3,
        }}
      >
        {renderContent()}
      </Box>
    </>
  );
};

export default RegisterMultiSteps;